/* PIMPO — comportamiento de la ficha de producto (plantilla product.pimpo) y del carrito.
   Sin dependencias. Cada init() es idempotente y se repite al recargar una sección
   en el personalizador (shopify:section:load). */
(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const once = (el, key) => {
    if (el.dataset[key]) return false;
    el.dataset[key] = '1';
    return true;
  };

  // Mismo formato que el filtro money de Liquid (Ajustes → Mercados/Moneda de la tienda).
  const moneyFormatter = (format) => {
    const amount = (cents, decimals, thousands, decimal) => {
      const [int, frac] = (cents / 100).toFixed(decimals).split('.');
      return int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? decimal + frac : '');
    };
    const formats = {
      amount: (c) => amount(c, 2, ',', '.'),
      amount_no_decimals: (c) => amount(c, 0, ',', '.'),
      amount_with_comma_separator: (c) => amount(c, 2, '.', ','),
      amount_no_decimals_with_comma_separator: (c) => amount(c, 0, '.', ','),
      amount_with_apostrophe_separator: (c) => amount(c, 2, "'", '.'),
      amount_with_space_separator: (c) => amount(c, 2, ' ', ','),
    };
    const template = format || '{{amount_with_comma_separator}} €';
    return (cents) =>
      template.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => (formats[key] || formats.amount_with_comma_separator)(cents));
  };

  /* ---------- Barra de anuncios rotativa ---------- */
  const initAnnouncements = (scope) => {
    scope.querySelectorAll('[data-pimpo-rotate]').forEach((bar) => {
      if (!once(bar, 'ppReady')) return;
      const items = bar.querySelectorAll('.pp-announce__item');
      if (items.length < 2 || reducedMotion) return;
      let index = 0;
      let paused = false;
      bar.addEventListener('mouseenter', () => (paused = true));
      bar.addEventListener('mouseleave', () => (paused = false));
      bar.addEventListener('focusin', () => (paused = true));
      bar.addEventListener('focusout', () => (paused = false));
      setInterval(() => {
        if (paused || document.hidden) return;
        items[index].classList.remove('is-active');
        index = (index + 1) % items.length;
        items[index].classList.add('is-active');
      }, Number(bar.dataset.pimpoRotate) || 4000);
    });
  };

  /* ---------- Fecha de entrega (días laborables, sin sábados ni domingos) ---------- */
  const dateKey = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  const initDelivery = (scope) => {
    scope.querySelectorAll('[data-pimpo-delivery]').forEach((el) => {
      const holidays = new Set(
        (el.dataset.holidays || '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      );
      const isBusinessDay = (d) => d.getDay() !== 0 && d.getDay() !== 6 && !holidays.has(dateKey(d));
      const addBusinessDays = (from, days) => {
        const d = new Date(from);
        let added = 0;
        while (added < days) {
          d.setDate(d.getDate() + 1);
          if (isBusinessDay(d)) added += 1;
        }
        return d;
      };

      const now = new Date();
      const cutoff = Number(el.dataset.cutoff ?? 24);
      let base = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      // Fin de semana, festivo o pasada la hora de corte: el pedido sale el siguiente día laborable.
      if (!isBusinessDay(base) || now.getHours() >= cutoff) base = addBusinessDays(base, 1);

      const minDays = Number(el.dataset.min) || 1;
      const maxDays = Math.max(Number(el.dataset.max) || minDays, minDays);
      const fmt = new Intl.DateTimeFormat('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
      const label = (d) => fmt.format(d).replace(',', '');

      const template = el.dataset.template || 'Recíbelo entre el [inicio] y el [fin]';
      const values = { '[inicio]': label(addBusinessDays(base, minDays)), '[fin]': label(addBusinessDays(base, maxDays)) };
      el.textContent = '';
      template.split(/(\[inicio\]|\[fin\])/).forEach((part) => {
        if (values[part]) {
          const strong = document.createElement('strong');
          strong.textContent = values[part];
          el.append(strong);
        } else if (part) {
          el.append(part);
        }
      });
      el.hidden = false;
    });
  };

  /* ---------- Galería ---------- */
  const initGallery = (gallery) => {
    if (!once(gallery, 'ppReady')) return null;
    const track = gallery.querySelector('[data-gallery-track]');
    const thumbs = [...gallery.querySelectorAll('[data-gallery-thumb]')];
    if (!track) return null;
    const slides = [...track.children];

    const setActive = (index) => {
      thumbs.forEach((thumb, i) => {
        thumb.classList.toggle('is-active', i === index);
        if (i === index) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      slides.forEach((slide, i) => {
        if (i !== index) slide.querySelectorAll('video').forEach((v) => v.pause());
      });
    };

    const goTo = (index) => {
      track.scrollTo({ left: index * track.clientWidth, behavior: reducedMotion ? 'auto' : 'smooth' });
    };

    thumbs.forEach((thumb) => thumb.addEventListener('click', () => goTo(Number(thumb.dataset.galleryThumb))));

    let ticking = false;
    track.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          setActive(Math.round(track.scrollLeft / track.clientWidth));
          ticking = false;
        });
      },
      { passive: true }
    );

    track.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
      event.preventDefault();
      const current = Math.round(track.scrollLeft / track.clientWidth);
      const next = event.key === 'ArrowRight' ? current + 1 : current - 1;
      goTo(Math.max(0, Math.min(slides.length - 1, next)));
    });

    return {
      showMedia(mediaId) {
        const index = slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
        if (index > -1) goTo(index);
      },
    };
  };

  /* ---------- Ficha de producto: variantes, packs, sticky ---------- */
  const initProduct = (root) => {
    if (!once(root, 'ppReady')) return;
    const money = moneyFormatter(root.dataset.moneyFormat);
    const variantsJson = root.querySelector('[data-pimpo-variants-json]');
    const variants = variantsJson ? JSON.parse(variantsJson.textContent) : [];
    const idInput = root.querySelector('[data-pimpo-variant-input]');
    const qtyInput = root.querySelector('[data-pimpo-qty]');
    const atc = root.querySelector('[data-pimpo-atc]');
    const picker = root.querySelector('[data-pimpo-variants-picker]');
    const packs = root.querySelector('[data-pimpo-packs]');
    const priceBox = root.querySelector('[data-pimpo-price]');
    const sticky = root.querySelector('[data-pimpo-sticky]');
    const galleryEl = root.querySelector('[data-pimpo-gallery]');
    const gallery = galleryEl ? initGallery(galleryEl) : null;

    let variant = variants.find((v) => String(v.id) === idInput?.value) || variants[0];

    const packPrices = (price) => {
      const discount = Number(packs?.dataset.discount) || 0;
      const compare = price * 2;
      const hasDiscount = discount > 0 && compare - discount > 0;
      const total = hasDiscount ? compare - discount : compare;
      return {
        hasDiscount,
        discount,
        compare,
        total,
        unit: Math.round(total / 2),
        pct: compare ? Math.round((discount * 100) / compare) : 0,
      };
    };

    const selectedQty = () => Number(packs?.querySelector('input:checked')?.value || qtyInput?.value || 1);

    const updateSticky = () => {
      if (!sticky || !variant) return;
      const price = sticky.querySelector('[data-sticky-price]');
      if (price) price.textContent = money(selectedQty() === 2 ? packPrices(variant.price).total : variant.price);
    };

    const updatePacks = () => {
      if (!packs || !variant) return;
      const p = packPrices(variant.price);
      const set = (selector, text, visible = true) => {
        const el = packs.querySelector(selector);
        if (!el) return;
        el.textContent = text;
        el.hidden = !visible;
      };
      set('[data-pack-price="1"]', money(variant.price));
      set('[data-pack-price="2"]', money(p.total));
      set('[data-pack-compare]', money(p.compare), p.hasDiscount);
      set('[data-pack-unit]', (packs.dataset.unitTemplate || '[unidad]/ud').replace('[unidad]', money(p.unit)), p.hasDiscount);
      set(
        '[data-pack-save]',
        (packs.dataset.saveTemplate || '').replace('[ahorro]', money(p.discount)).replace('[porcentaje]', p.pct),
        p.hasDiscount
      );
    };

    const updatePrice = () => {
      if (!priceBox || !variant) return;
      const onSale = variant.compare_at_price > variant.price;
      priceBox.querySelector('[data-price-current]').textContent = money(variant.price);
      const compare = priceBox.querySelector('[data-price-compare]');
      const badge = priceBox.querySelector('[data-price-badge]');
      compare.hidden = !onSale;
      badge.hidden = !onSale;
      if (onSale) {
        compare.textContent = money(variant.compare_at_price);
        const pct = Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price);
        badge.textContent = (priceBox.dataset.badgeTemplate || 'AHORRA [porcentaje]%').replace('[porcentaje]', pct);
      }
    };

    const updateButtons = () => {
      const available = Boolean(variant && variant.available);
      const label = !variant ? 'No disponible' : available ? atc?.dataset.label : 'Agotado';
      if (atc) {
        atc.disabled = !available;
        const span = atc.querySelector('span');
        if (span) span.textContent = label;
      }
      const stickyBtn = sticky?.querySelector('[data-sticky-button]');
      if (stickyBtn) {
        stickyBtn.disabled = !available;
        stickyBtn.textContent = label;
      }
    };

    const applyVariant = () => {
      updateButtons();
      if (!variant) return;
      if (idInput) idInput.value = variant.id;
      updatePrice();
      updatePacks();
      updateSticky();

      const image = sticky?.querySelector('[data-sticky-image]');
      if (image && variant.featured_image?.src) {
        image.removeAttribute('srcset');
        image.src = `${variant.featured_image.src}${variant.featured_image.src.includes('?') ? '&' : '?'}width=120`;
      }
      if (variant.featured_media && gallery) gallery.showMedia(variant.featured_media.id);

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    };

    picker?.addEventListener('change', (event) => {
      const fieldsets = [...picker.querySelectorAll('fieldset')];
      const selected = fieldsets.map((fs) => fs.querySelector('input:checked')?.value);
      const legend = picker.querySelector(`[data-option-selected="${event.target.dataset.optionIndex}"]`);
      if (legend) legend.textContent = event.target.value;
      variant = variants.find((v) => v.options.every((value, i) => value === selected[i]));
      applyVariant();
    });

    packs?.addEventListener('change', () => {
      if (qtyInput) qtyInput.value = selectedQty();
      updateSticky();
    });
    if (qtyInput && packs) qtyInput.value = selectedQty();

    // Barra fija: visible cuando el botón principal ya ha quedado por encima de la pantalla.
    if (sticky && atc && 'IntersectionObserver' in window) {
      const stickyBtn = sticky.querySelector('[data-sticky-button]');
      new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        sticky.classList.toggle('is-visible', show);
        sticky.setAttribute('aria-hidden', String(!show));
        if (stickyBtn) stickyBtn.tabIndex = show ? 0 : -1;
      }).observe(atc);
    }
  };

  /* ---------- FAQ: solo una abierta (respaldo del atributo name de <details>) ---------- */
  document.addEventListener(
    'toggle',
    (event) => {
      const details = event.target;
      if (!(details instanceof HTMLDetailsElement) || !details.open) return;
      const group = details.closest('[data-pimpo-single]');
      if (!group) return;
      group.querySelectorAll('details[open]').forEach((other) => {
        if (other !== details) other.open = false;
      });
    },
    true
  );

  /* ---------- Vídeos UGC: autoplay en silencio solo en pantalla ---------- */
  const ugcObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) =>
            entries.forEach(({ target, isIntersecting }) => {
              if (isIntersecting) target.play().catch(() => {});
              else target.pause();
            }),
          { threshold: 0.5 }
        )
      : null;

  const initUgc = (scope) => {
    scope.querySelectorAll('[data-pimpo-ugc] video').forEach((video) => {
      if (!once(video, 'ppReady')) return;
      video.muted = true;
      if (reducedMotion || !ugcObserver) {
        video.controls = true;
        return;
      }
      ugcObserver.observe(video);
    });
  };

  /* ---------- Carrito lateral: subir Pimpo a 2 unidades ---------- */
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-pimpo-upsell]');
    if (!button) return;
    event.preventDefault();
    button.setAttribute('aria-busy', 'true');

    const drawer = document.querySelector('cart-drawer');
    try {
      const response = await fetch(`${window.routes?.cart_change_url || '/cart/change'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: button.dataset.pimpoUpsell,
          quantity: Number(button.dataset.quantity) || 2,
          sections: ['cart-drawer', 'cart-icon-bubble'],
          sections_url: window.location.pathname,
        }),
      });
      const state = await response.json();
      if (!response.ok || state.status) throw new Error(state.description || 'cart/change');

      if (drawer && typeof drawer.renderContents === 'function' && state.sections?.['cart-drawer']) {
        drawer.renderContents(state);
      }
      const bubble = document.getElementById('cart-icon-bubble');
      const bubbleHtml = state.sections?.['cart-icon-bubble'];
      if (bubble && bubbleHtml) {
        const section = new DOMParser().parseFromString(bubbleHtml, 'text/html').querySelector('.shopify-section');
        if (section) bubble.innerHTML = section.innerHTML;
      }
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'pimpo-upsell', cartData: state });
      }
    } catch (error) {
      console.error('[pimpo] upsell', error);
      window.location.href = window.routes?.cart_url || '/cart';
    } finally {
      button.removeAttribute('aria-busy');
    }
  });

  /* ---------- Hero: flecha que baja a la siguiente sección ---------- */
  document.addEventListener('click', (event) => {
    const arrow = event.target.closest('[data-pimpo-scroll-next]');
    if (!arrow) return;
    const current = arrow.closest('.shopify-section');
    const next = current && current.nextElementSibling;
    if (next) next.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
  });

  /* ---------- Reseñas: "Ver más" y foto ampliada ---------- */
  document.addEventListener('click', (event) => {
    const more = event.target.closest('[data-pimpo-reviews-more]');
    if (more) {
      const grid = more.closest('.pp-revs')?.querySelector('[data-pimpo-reviews]');
      if (!grid) return;
      grid.classList.add('is-expanded');
      const first = grid.querySelector('.pp-revs__card.is-extra');
      more.closest('.pp-revs__more')?.remove();
      if (first) {
        first.setAttribute('tabindex', '-1');
        first.focus({ preventScroll: true });
      }
      return;
    }

    const photo = event.target.closest('[data-pimpo-lightbox]');
    const dialog = photo?.closest('.pp-revs')?.querySelector('[data-pimpo-lightbox-dialog]');
    if (photo && dialog && typeof dialog.showModal === 'function') {
      const img = dialog.querySelector('img');
      img.src = photo.dataset.pimpoLightbox;
      img.alt = photo.dataset.alt || '';
      dialog.showModal();
      return;
    }

    const openDialog = event.target.closest('[data-pimpo-lightbox-dialog]');
    if (openDialog && (event.target === openDialog || event.target.closest('[data-pimpo-lightbox-close]'))) openDialog.close();
  });

  /* ---------- Clics medibles en Clarity ---------- */
  document.addEventListener('click', (event) => {
    const tracked = event.target.closest('[data-pimpo-track]');
    if (!tracked) return;
    // El clic en una <label> se repite sobre su <input>: contamos solo uno.
    if (event.target.matches('input') && tracked.tagName === 'LABEL') return;
    if (typeof window.clarity === 'function') window.clarity('event', `pimpo_${tracked.dataset.pimpoTrack}`);
  });

  /* ---------- Puntitos de los carruseles (galería, mini-testimonios, UGC, fotos de reseñas) ---------- */
  const DOT_TRACKS = [
    ['.pp-gallery__track', 'pp-dots--overlay', 'foto'],
    ['.pp-minitesti__track', '', 'opinión'],
    ['.pp-ugc__track', '', 'vídeo'],
    ['.pp-revs__photos', '', 'foto'],
  ];

  const initDots = (scope) => {
    DOT_TRACKS.forEach(([selector, variant, noun]) => {
      scope.querySelectorAll(selector).forEach((track) => {
        if (!once(track, 'ppDots')) return;
        const dots = document.createElement('div');
        dots.className = `pp-dots ${variant}`.trim();
        const inner = document.createElement('div');
        inner.className = 'pp-dots__inner';
        dots.append(inner);
        track.after(dots);

        let stops = [];
        const items = () => [...track.children].filter((el) => el.offsetParent !== null);

        const build = () => {
          const list = items();
          const max = track.scrollWidth - track.clientWidth;
          const pad = parseFloat(getComputedStyle(track).scrollPaddingLeft) || 0;
          const base = track.getBoundingClientRect().left - track.scrollLeft;
          // Una parada por elemento, sin pasar del final y sin repetir posiciones.
          const raw = list.map((el) => Math.min(max, Math.max(0, Math.round(el.getBoundingClientRect().left - base - pad))));
          stops = raw.filter((x, i) => i === 0 || Math.abs(x - raw[i - 1]) > 4);
          inner.textContent = '';
          dots.hidden = max < 8 || stops.length < 2;
          if (dots.hidden) return;
          stops.forEach((x, i) => {
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.className = 'pp-dots__dot';
            dot.setAttribute('aria-label', `Ir a la ${noun} ${i + 1} de ${stops.length}`);
            dot.addEventListener('click', () => track.scrollTo({ left: x, behavior: reducedMotion ? 'auto' : 'smooth' }));
            inner.append(dot);
          });
          update();
        };

        const update = () => {
          if (dots.hidden) return;
          const left = track.scrollLeft;
          let active = 0;
          stops.forEach((x, i) => {
            if (Math.abs(x - left) < Math.abs(stops[active] - left)) active = i;
          });
          inner.querySelectorAll('.pp-dots__dot').forEach((dot, i) => {
            dot.classList.toggle('is-active', i === active);
            if (i === active) dot.setAttribute('aria-current', 'true');
            else dot.removeAttribute('aria-current');
          });
        };

        let ticking = false;
        track.addEventListener(
          'scroll',
          () => {
            if (ticking) return;
            ticking = true;
            requestAnimationFrame(() => {
              update();
              ticking = false;
            });
          },
          { passive: true }
        );
        let resizeTimer;
        window.addEventListener('resize', () => {
          clearTimeout(resizeTimer);
          resizeTimer = setTimeout(build, 150);
        });
        // Las imágenes lazy cambian el ancho al cargar: recalcular cuando terminen.
        track.querySelectorAll('img, video').forEach((media) => media.addEventListener('load', () => setTimeout(build, 50), { once: true }));
        build();
      });
    });
  };

  /* ---------- Línea de valoración bajo el título (datos reales de #pimpo-opiniones) ---------- */
  const initRatingLine = (scope) => {
    scope.querySelectorAll('[data-pimpo-rating-auto]').forEach((line) => {
      const source = document.getElementById('pimpo-opiniones');
      const count = Number(source?.dataset.count) || 0;
      const avg = Number(source?.dataset.rating) || 0;
      if (!count || !avg) {
        line.remove();
        return;
      }
      const value = avg.toLocaleString('es-ES', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
      line.querySelector('.pp-stars').style.setProperty('--pp-fill', `${(avg / 5) * 100}%`);
      line.querySelector('[data-rating-text]').innerHTML =
        `<strong>${value}/5</strong> · ${count} ${count === 1 ? 'reseña' : 'reseñas'}` +
        `<span class="visually-hidden"> (valoración media de ${value} sobre 5)</span>`;
      line.classList.add('is-ready');
    });
  };

  /* ---------- Arranque ---------- */
  const init = (scope = document) => {
    initAnnouncements(scope);
    initDelivery(scope);
    scope.querySelectorAll('[data-pimpo-product]').forEach(initProduct);
    initUgc(scope);
    initDots(scope);
    initRatingLine(scope);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
